import React, { useState, useEffect } from 'react';
import { customerAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import ErrorMessage from '../components/Common/ErrorMessage';
import SuccessMessage from '../components/Common/SuccessMessage';

const CustomerManagement = () => {
  const { isSuperAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    contact_email: '',
    contact_phone: '',
  });

  useEffect(() => {
    if (isSuperAdmin) {
      loadCustomers();
    } else {
      setError('Access denied. Only super administrators can manage customers.');
      setLoading(false);
    }
  }, [isSuperAdmin]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await customerAPI.getAll();
      setCustomers(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      if (editingCustomer) {
        await customerAPI.update(editingCustomer.id, formData);
        setSuccess('Customer updated successfully');
      } else {
        await customerAPI.create(formData);
        setSuccess('Customer created successfully');
      }

      await loadCustomers();
      setShowForm(false);
      setEditingCustomer(null);
      setFormData({ name: '', code: '', contact_email: '', contact_phone: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save customer');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      code: customer.code,
      contact_email: customer.contact_email || '',
      contact_phone: customer.contact_phone || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer? This will also delete all associated groups, users, and data.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await customerAPI.delete(id);
      setSuccess('Customer deleted successfully');
      await loadCustomers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete customer');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCustomer(null);
    setFormData({ name: '', code: '', contact_email: '', contact_phone: '' });
  };

  if (!isSuperAdmin) {
    return (
      <div className="page-container">
        <ErrorMessage message="Access denied. Only super administrators can manage customers." />
      </div>
    );
  }

  if (loading && customers.length === 0) {
    return <LoadingSpinner text="Loading customers..." />;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Customer Management</h2>
        <button className="btn btn-primary" onClick={() => {
          setEditingCustomer(null);
          setFormData({ name: '', code: '', contact_email: '', contact_phone: '' });
          setShowForm(true);
        }}>
          + Create Customer
        </button>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError(null)} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess(null)} />}

      {/* Create/Edit Customer Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleCancel();
          }
        }}>
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>{editingCustomer ? 'Edit Customer' : 'Create New Customer'}</h3>
              <button 
                className="btn-close" 
                onClick={handleCancel}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
              <div className="form-group">
                <label>Customer Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Customer Code *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  required
                  disabled={!!editingCustomer}
                />
                <small className="form-text text-muted">Unique code for this customer (cannot be changed after creation)</small>
              </div>
              <div className="form-group">
                <label>Contact Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Contact Phone</label>
                <input
                  type="tel"
                  className="form-control"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingCustomer ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3>Customers</h3>
        </div>
        <div className="card-body">
          {customers.length === 0 ? (
            <p>No customers found. Create your first customer to get started.</p>
          ) : (
            <div className="table-container">
              <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Contact Email</th>
                  <th>Contact Phone</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id}>
                    <td>{customer.name}</td>
                    <td><code>{customer.code}</code></td>
                    <td>{customer.contact_email || 'N/A'}</td>
                    <td>{customer.contact_phone || 'N/A'}</td>
                    <td>
                      <span className={`badge ${customer.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {customer.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="btn-group">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleEdit(customer)}
                        >
                            <i class="fa-solid fa-pen-to-square"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(customer.id)}
                        >
                          <i class="fa-solid fa-trash-can"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerManagement;

